import { useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faCheckCircle,
  faGift,
  faExclamationCircle,
  faTicket,
} from "@fortawesome/free-solid-svg-icons";
import { redeemCredits } from "../../services/api";
import { redeemFailureMessage } from "../../utils/redeemError";
import { useAuth } from "../../contexts/AuthContext";
import { BANNED_MESSAGE } from "../../utils/roles";

/**
 * Spend RoamPoints on a reward.
 *
 * Moved here from the standalone /credits page when that page was merged into
 * Account → Wallet. This is the only place RoamPoints can be spent, so it has
 * to travel with the merge — the wallet tab otherwise shows a balance with no
 * way to use it.
 *
 * `amount` values are the product prices the redeem endpoint enforces; the
 * server is still the authority and rejects an underfunded redemption with 402.
 */
const PRODUCTS = [
  { key: "travel_coupon", title: "Travel Coupon", desc: "A coupon for your next RoamMyWay trip booking", amount: 500, icon: faTicket, color: "text-teal-600", bg: "bg-teal-100" },
];

const CLOSED = { isOpen: false, step: "confirm", product: null, code: null, error: null };

export default function RedeemRewards({ balance = 0, onRedeemed }) {
  const [modalState, setModalState] = useState(CLOSED);
  const [redeeming, setRedeeming] = useState(false);
  const { isBanned } = useAuth();

  const openRedeemModal = (product) => {
    if (isBanned) {
      toast.error(BANNED_MESSAGE);
      return;
    }
    if (balance < product.amount) {
      toast.error(`You need ${product.amount} RoamPoints to redeem this.`);
      return;
    }
    setModalState({ ...CLOSED, isOpen: true, product });
  };

  const handleConfirmRedeem = async () => {
    const { product } = modalState;
    if (!product) return;

    try {
      setRedeeming(true);
      setModalState((prev) => ({ ...prev, error: null }));
      const response = await redeemCredits(product.key, product.amount);

      setModalState((prev) => ({
        ...prev,
        step: "success",
        code: response.couponCode,
        valueInr: response.valueInr,
        expiresAt: response.expiresAt,
      }));

      // Balance and ledger both changed — let the wallet tab reload them.
      onRedeemed?.();
      toast.success("Coupon issued! Copy the code and use it at checkout.");
    } catch (err) {
      if (err.status === 401) {
        toast.error("Session expired. Please log in.");
        return;
      }
      const errorMessage = redeemFailureMessage(err, { shortBy: product.amount - balance });
      setModalState((prev) => ({ ...prev, error: errorMessage }));
    } finally {
      setRedeeming(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Code copied to clipboard!");
  };

  return (
    <>
      <div className="mt-8">
        <h3 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FontAwesomeIcon icon={faGift} className="text-teal-600" />
          Redeem Rewards
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {PRODUCTS.map((product) => {
            const affordable = balance >= product.amount && !isBanned;
            return (
              <button
                key={product.key}
                type="button"
                onClick={() => affordable && openRedeemModal(product)}
                disabled={!affordable}
                className={`bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center justify-between gap-4 text-left transition-all w-full ${
                  affordable
                    ? "hover:shadow-md cursor-pointer hover:border-teal-200 hover:bg-teal-50/30"
                    : "opacity-75 cursor-not-allowed grayscale-[0.2]"
                }`}
              >
                <div className="flex items-center gap-4 min-w-0">
                  <span className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${product.bg} ${product.color}`}>
                    <FontAwesomeIcon icon={product.icon} className="text-xl" />
                  </span>
                  <span className="min-w-0">
                    <span className="block font-bold text-gray-900 text-sm">{product.title}</span>
                    <span className="block text-xs text-gray-500 leading-tight mt-0.5">{product.desc}</span>
                  </span>
                </div>
                <span className="shrink-0 text-right">
                  <span className="block font-bold text-teal-700 text-sm">{product.amount}</span>
                  <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                    RoamPoints
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {modalState.isOpen && modalState.product && (
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-sm w-full shadow-2xl relative border border-gray-100">
            {modalState.step === "confirm" ? (
              <div className="text-center">
                <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${modalState.product.bg} ${modalState.product.color}`}>
                  <FontAwesomeIcon icon={modalState.product.icon} className="text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Redeem Reward</h3>
                <p className="text-gray-600 mb-6">
                  Are you sure you want to spend{" "}
                  <strong className="text-teal-700">{modalState.product.amount} RoamPoints</strong> for the{" "}
                  <strong>{modalState.product.title}</strong>?
                </p>
                {modalState.error && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-xl mb-6 text-sm flex items-start gap-2 text-left">
                    <FontAwesomeIcon icon={faExclamationCircle} className="mt-0.5" />
                    <span>{modalState.error}</span>
                  </div>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={() => setModalState(CLOSED)}
                    disabled={redeeming}
                    className="flex-1 py-3 px-4 font-semibold text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmRedeem}
                    disabled={redeeming}
                    className="flex-1 py-3 px-4 font-semibold text-white bg-teal-600 rounded-xl hover:bg-teal-700 transition-colors disabled:opacity-80 flex items-center justify-center"
                  >
                    {redeeming ? (
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    ) : (
                      "Confirm"
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-green-100 text-green-600">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-4xl" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Success!</h3>
                <p className="text-gray-600 mb-6 text-sm">
                  {Number.isFinite(modalState.valueInr)
                    ? `Your coupon is worth ₹${modalState.valueInr.toLocaleString("en-IN")}. Here is your code:`
                    : "Your reward has been redeemed. Here is your unique code:"}
                </p>

                <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-4 mb-6 flex items-center justify-between">
                  <code className="text-xl font-black text-gray-900 tracking-wider">
                    {modalState.code || "PENDING"}
                  </code>
                  <button
                    onClick={() => copyToClipboard(modalState.code)}
                    className="text-teal-600 hover:text-teal-800 p-2 rounded-lg hover:bg-teal-50 transition-colors cursor-pointer"
                    title="Copy code"
                  >
                    <FontAwesomeIcon icon={faCopy} className="text-lg" />
                  </button>
                </div>

                {modalState.expiresAt && (
                  <p className="text-sm text-gray-500 mb-6">
                    Expires on:{" "}
                    <span className="font-semibold text-gray-700">
                      {new Date(modalState.expiresAt).toLocaleDateString()}
                    </span>
                  </p>
                )}

                <button
                  onClick={() => setModalState(CLOSED)}
                  className="w-full py-3 px-4 font-semibold text-white bg-teal-600 rounded-xl hover:bg-teal-700 transition-colors"
                >
                  Done
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
